import { Component, inject, input, model, output, signal } from '@angular/core';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs';

import { ICONS } from '@gfe/core/services/icons/icons.service';
import { NotificationService } from '@gfe/core/services/notification/notification.service';
import { PlansService } from '@gfe/core/services/plans/plans.service';
import { ModalComponent, ModalConfig } from '@gfe/shared/components/modal/modal.component';

import {
  PlanDetails,
  PlanDetailsFormComponent
} from '@features/components/plan/plan-details-form/plan-details-form.component';

/**
 * Self-contained rename UX: the form modal, the backend call and the result notifications.
 * The parent only opens it and reacts to `renamed` (e.g. refetch the list).
 */
@Component({
  selector: 'app-rename-plan-flow',
  imports: [ModalComponent, PlanDetailsFormComponent, TranslatePipe],
  templateUrl: './rename-plan-flow.component.html',
  styles: ``
})
export class RenamePlanFlowComponent {
  private readonly plansService = inject(PlansService);
  private readonly notificationService = inject(NotificationService);
  private readonly translate = inject(TranslateService);

  planId = input<string>('');
  currentName = input<string>('');
  currentDescription = input<string>('');

  /** Two-way: the parent opens the flow; the flow closes itself on cancel/success. */
  open = model<boolean>(false);

  renamed = output<void>();

  protected readonly isSaving = signal(false);

  protected readonly ICONS = ICONS;

  protected readonly modalConfig: ModalConfig = {
    dismissible: true,
    closeOnBackdropClick: true
  };

  protected closeForm(): void {
    if (this.isSaving()) {
      return;
    }
    this.open.set(false);
  }

  protected onRename(data: PlanDetails): void {
    const planId = this.planId();
    if (!planId || this.isSaving()) {
      return;
    }

    this.isSaving.set(true);
    this.plansService
      .updatePlan$({ planId, name: data.name, description: data.description })
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.open.set(false);
          this.notificationService.success(
            this.translate.instant('features.plan.rename.title'),
            this.translate.instant('features.plan.rename.success')
          );
          this.renamed.emit();
        },
        error: (error) => {
          console.error('Error renaming plan:', error);
          this.isSaving.set(false);
          this.notificationService.error(
            this.translate.instant('features.plan.rename.title'),
            this.translate.instant('features.plan.rename.error')
          );
        }
      });
  }
}
